import { CommonApiService } from '@services/common-api-service';
import { Component, Event, EventEmitter, Host, Prop, State, h } from '@stencil/core';
import { pathStore } from '@store/path-store';
import { If } from '@utils/If';

@Component({
  tag: 'login-flow',
  styleUrl: 'login-flow.scss',
  shadow: true
})

export class LoginFlow {
  private ACCOUNT_BLOCKED = 'S004';
  @Prop() isLoginRequired: boolean = false;
  @Event() success: EventEmitter<any>;
  @Event() cancel: EventEmitter<void>;
  @State() validateOtp: boolean = false;
  @State() sendOtpLoading: boolean = false;
  @State() otpVerifying: boolean = false;
  @State() loginId: string = '';
  @State() mobileNumber: any = '';
  @State() otp: any;
  @State() error: any = {};
  @State() resendOtpTimer: number = 30; // Timer starts at 30 seconds
  @State() canResendOtp: boolean = false; // Controls whether the "Resend OTP" button is enabled
  @State() isAccountBlocked: boolean = false;
  @State() interval: any;
  @State() resendOtpLoading: boolean = false;
  @State() phoneNumberSubmitted: boolean = false;
  @State() otpSubmitted: boolean = false;
  @State() otpAttempts: { attempt: number; maxAttempt: number } = {
    attempt: 0,
    maxAttempt: 3
  };
  @State() resendAttempts: { attempt: number; maxAttempt: number } = {
    attempt: 0,
    maxAttempt: 4
  };
  private otpInputRef?: HTMLOtpInputElement;
  private apiService = new CommonApiService();

  onPhoneNumberInput = (event: any) => {
    const { mobile, target } = event.detail;
    if (this.error['mobile'] && !/^\d{10}$/.test(mobile)) {
      this.error = { ...this.error, mobile: 'Please enter a valid mobile number.' };
    } else {
      this.error = { ...this.error, mobile: '' };
    }
    this.mobileNumber = mobile;
    this.isAccountBlocked = false;
    if (!this.phoneNumberSubmitted && this.mobileNumber.length === 10) {
      this.sendOtp(event);
      target.blur();
    }
  };

  onOTPInput = (event: any) => {
    if (this.error['otp'] && !/^\d{6}$/.test(event.detail)) {
      this.error = { ...this.error, otp: 'Please enter a valid OTP' };
    } else {
      this.error = { ...this.error, otp: '' };
    }
    this.otp = event.detail;
  };

  onOtpComplete = (event: any) => {

    if (!this.otpSubmitted && this.otp.length === 6) {
      this.verifyCustomer(event);
    }
  };

  editNumber = () => {
    this.validateOtp = false;
    this.error = {};
  };

  onCanelLogin = (event: any) => {
    event.stopPropagation();
    this.cancel.emit();
  };

  startResendOtpTimer = () => {
    clearInterval(this.interval);
    this.resendOtpTimer = this.DEFAULT_OTP_TIMER; // Reset timer to default value

    this.interval = setInterval(() => {
      if (this.resendOtpTimer > 0) {
        this.resendOtpTimer -= 1;
      } else {
        clearInterval(this.interval);
        this.canResendOtp = true;
      }
    }, 1000);
  };

  resendOtp = async () => {
    try {
      if (this.canResendOtp) {
        this.resendOtpLoading = true;
        this.error = {};
        this.otpInputRef.clear();
        this.otp = '';
        const body = { newusername: this.mobileNumber, countrycode: 91, type: 'login_otp', generateNew: false, emailLogIn: false, forgotPassword: false };
        const result = await this.apiService.post('/generate_otp', body);
        if (result.result) {
          let parsedResult = result.result
          try {
            parsedResult = JSON.parse(parsedResult);
          } catch (e) { }

          let { data } = parsedResult;
          this.otpAttempts = {
            attempt: data.maxValidationAttempt - data.validationAttempt,
            maxAttempt: data.maxValidationAttempt
          };
          this.resendAttempts = {
            attempt: result.attempt,
            maxAttempt: result.maxAttempt
          };

        }
        this.otpInputRef
        if (!result?.status) {
          this.error['generic'] = result.msg || 'Something went wrong!';
        }
        this.startResendOtpTimer(); // Restart the timer
        this.canResendOtp = false;
      }
    } catch (error) {
      this.error['generic'] = 'Something went wrong! Try after sometime.';
    } finally {
      this.resendOtpLoading = false;
    }
  };

  sendOtp = async (event: any) => {
    event.preventDefault();
    if (this.sendOtpLoading) return;
    if (!this.mobileNumber) {
      this.error = { ...this.error, mobile: 'Please enter mobile number.' };
      return;
    } else if (!/^[6-9]\d{9}$/.test(this.mobileNumber)) {
      this.error = { ...this.error, mobile: 'Please enter a valid mobile number.' };
      return;
    } else if (this.error['mobile']) {
      return;
    }
    try {
      this.sendOtpLoading = true;
      const body = { newusername: this.mobileNumber, countrycode: 91, type: 'login_otp', generateNew: true, emailLogIn: false, forgotPassword: false };
      const result = await this.apiService.post('/generate_otp', body);

      if (result?.status) {

        this.validateOtp = true;
        this.loginId = result.loginId;
        this.otpAttempts = {
          attempt: result.maxValidationAttempt - result.validationAttempt,
          maxAttempt: result.maxValidationAttempt
        };

        this.resendAttempts = {
          attempt: result.attempt,
          maxAttempt: result.maxAttempt
        };
        this.startResendOtpTimer();
        this.error['generic'] = '';
      } else {
        this.error['mobile'] = result.msg || 'Oops! We’re having trouble sending OTPs';
        if (this.ACCOUNT_BLOCKED) {
          this.isAccountBlocked = true;
        }
      }
    } catch (error) {
      console.log(error);
      this.error['generic'] = 'We\'re having a tech hiccup. Hang tight!';
    } finally {
      this.sendOtpLoading = false;
      this.phoneNumberSubmitted = true;
    }
  };

  isValidJson = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  verifyCustomer = async (event: any) => {
    event.preventDefault();
    if (this.otpVerifying) return;

    if (!this.otp) {
      this.error = { ...this.error, otp: 'Please enter OTP.' };
      return;
    } else if (!/^\d{6}$/.test(this.otp)) {
      this.error = { ...this.error, otp: 'Please enter a valid OTP.' };
    }
    try {

      this.otpVerifying = true;
      const body = {
        loginId: this.loginId,
        otpcode: this.otp
      };
      const result = await this.apiService.post('/otp_based_login', body, {}, true);
      if (result.status) {
        const parsed = JSON.parse(result.result);
        this.success.emit({ ...parsed, mobileNumber: this.mobileNumber, respHeaders: result.headers });
      } else {
        if (result.erroCode === this.ACCOUNT_BLOCKED) {
          this.error['generic'] = result.message;
          this.isAccountBlocked = true;
          this.otpAttempts = {
            attempt: 0,
            maxAttempt: 3
          };
        } else {
          if (result.result) {
            let parsedResult = result.result
            try {
              parsedResult = JSON.parse(parsedResult);
            } catch (e) { }

            let { data } = parsedResult;
            this.otpAttempts = {
              attempt: data.maxValidationAttempt - data.validationAttempt,
              maxAttempt: data.maxValidationAttempt
            };
            this.resendAttempts = {
              attempt: data.attempt,
              maxAttempt: data.maxAttempt
            };
            if (data.errorCode == this.ACCOUNT_BLOCKED) {
              this.isAccountBlocked = true;
            }
          }
          this.error['otp'] = result.message || result.msg || 'We\'re having a tech hiccup. Hang tight!';

        }
      }
    } catch (error) {
      this.error['generic'] = 'Uh-oh! Something went wrong on our side.';
    } finally {
      this.otpVerifying = false;
      this.otpSubmitted = true;
    }
  };
  private DEFAULT_OTP_TIMER: number = 30;

  render() {
    const assetPath = pathStore.assetPath;
    return (
      <Host>
        <div class={`flex flex-col relative md:h-full gap-7 md:gap-8 ${this.validateOtp ? 'justify-between' : 'justify-center'}`}>
          <If
            condition={!this.isLoginRequired}
            children={<div onClick={this.onCanelLogin} class="absolute md:hidden -right-2 -top-2">
              <img src={assetPath + 'icons/roundedClose.svg'} />
            </div>}
          />
          <div class="flex flex-col gap-5">
            <div class="flex flex-col items-center">
              <If condition={!this.validateOtp} children={<img class="md:hidden mb-6" src={assetPath + 'icons/authFlow.svg'} />} />
              <If condition={this.validateOtp} children={<img class="md:hidden mb-6" src={assetPath + 'icons/verifyOtp.svg'} />} />
              <h5 class="font-semibold text-black text-2xl">{!this.validateOtp ? 'Let\'s get started' : 'Enter OTP'}</h5>
              <span class="text-gray-600 mt-2 text-sm text-center md:w-70">
                {!this.validateOtp ? 'Enter your mobile number to sign in or create an account' : `A 6-digit OTP is sent to customer's mobile number *****${this.mobileNumber?.slice(-5)}.`}
              </span>
              <If
                condition={this.validateOtp}
                children={
                  <p onClick={this.editNumber} class="m-0 select-none mt-2 text-sm text-sb-primary cursor-pointer font-semibold">
                    Edit Mobile Number
                  </p>
                }
              />
            </div>
            <If
              condition={!this.validateOtp}
              children={
                <form onSubmit={this.sendOtp}>
                  <phone-input
                    value={this.mobileNumber}
                    error={this.error['mobile']}
                    onValueChanged={this.onPhoneNumberInput}
                    autoFocus
                    class="mt-6"
                    label="Mobile Number"
                    name="number"
                  />
                  <input type="submit" class="hidden" />
                </form>
              }
            />
            <If
              condition={this.validateOtp}
              children={
                <div class="mt-6">
                  <form onSubmit={this.verifyCustomer}>
                    <otp-input
                      ref={el => (this.otpInputRef = el as HTMLOtpInputElement)}
                      error={this.error['otp']}
                      name="number"
                      autoSubmit
                      onOtpComplete={this.onOtpComplete}
                      onValueChange={this.onOTPInput}
                    />
                    <input type="submit" class="hidden" />
                  </form>
                  <div class="flex mt-3 justify-between text-gray-500 text-sm">
                    {this.canResendOtp ? (
                      this.resendOtpLoading ? (
                        <span class="spinner ml-8" />
                      ) : (
                        ((this.resendAttempts.maxAttempt - this.resendAttempts.attempt) > 0 && !this.isAccountBlocked) ?
                          <p onClick={this.resendOtp} class="m-0 select-none text-sm text-sb-primary cursor-pointer font-semibold">
                            Resend OTP ( {this.resendAttempts.maxAttempt - this.resendAttempts.attempt} left)
                          </p> : <p class="m-0 select-none text-sm cursor-not-allowed font-semibold">
                            Resend OTP
                          </p>

                      )
                    ) : (
                      <span>
                        Resend OTP in: <span class="text-black font-semibold">{this.resendOtpTimer} sec</span>
                      </span>
                    )}

                    {this.otpAttempts.attempt == 2 || this.otpAttempts.attempt == 1 ? <span>
                      {this.otpAttempts.attempt} / {this.otpAttempts.maxAttempt} attempts left
                    </span> : ""}
                  </div>
                </div>
              }
            />
          </div>
          <div>
            <If condition={!!this.error['generic']} children={<p class="text-sb-danger font-medium text-center mt-1 mb-2 text-xs">{this.error['generic']}</p>} />
            <div class={`flex flex-col md:flex-row gap-4 ${!this.validateOtp ? 'pb-5 md:pb-0' : ''}`}>
              <If
                condition={!this.isLoginRequired}
                children={
                  <custom-button onButtonClick={this.onCanelLogin} class="flex-1 hidden md:flex" variant="outlined">
                    {this.validateOtp ? 'Cancel' : 'Close'}
                  </custom-button>
                }
              />

              <custom-button
                isLoading={this.validateOtp ? this.otpVerifying : this.sendOtpLoading}
                disabled={
                  (!this.validateOtp &&
                    (this.sendOtpLoading || this.mobileNumber?.length !== 10 || !!this.error['mobile'])) ||
                  (this.validateOtp && (this.otpAttempts.attempt >= this.otpAttempts.maxAttempt || this.otpVerifying || this.otp?.length !== 6 || !!this.error['otp']))
                }
                class="flex-1"
                id='submit-button'
                type="submit"
                onButtonClick={this.validateOtp ? this.verifyCustomer : this.sendOtp}
              >
                {this.validateOtp ? 'Verify' : 'Send OTP'}
              </custom-button>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
